import type { ChecklistTemplate } from '@/types/checklist-template';
import { TaskCategory, TimingAnchor } from '@/types/task';
import { WeaponType } from '@/types/season';

const TEMPLATE_ID = 'builtin-lease-hunter-simplified';
const BASE_DATE = '2026-01-01T00:00:00.000Z';

const T = {
  confirmLease: 'lease-hunter-001',
  scoutProperty: 'lease-hunter-002',
  deployCameras: 'lease-hunter-003',
  cameraCardPull: 'lease-hunter-004',
  hangVerifyStands: 'lease-hunter-005',
  trimShootingLanes: 'lease-hunter-006',
  purchaseLicense: 'lease-hunter-007',
  gearTuneUp: 'lease-hunter-008',
  scentControlPrep: 'lease-hunter-009',
  finalCamPull: 'lease-hunter-010',
  accessRouteWalk: 'lease-hunter-011',
  packGear: 'lease-hunter-012',
} as const;

export const LEASE_HUNTER_TEMPLATE: ChecklistTemplate = {
  id: TEMPLATE_ID,
  name: 'Lease Hunter Simplified',
  description:
    'Streamlined preparation checklist for hunters who lease land or hunt without habitat control. Focuses on scouting, stand prep, cameras, gear, and regulatory tasks. No food plot or habitat management tasks — works for bow, gun, or muzzleloader.',
  weaponType: WeaponType.Any,
  region: 'any',
  isBuiltIn: true,
  version: 1,
  createdAt: BASE_DATE,
  updatedAt: BASE_DATE,
  tasks: [
    // ─── Opener -120: Confirm lease ──────────────────────────────────────────
    {
      id: T.confirmLease,
      templateId: TEMPLATE_ID,
      title: 'Confirm lease agreement and access dates',
      description: '',
      category: TaskCategory.RegulatoryAdmin,
      timingAnchor: TimingAnchor.OpenerRelative,
      offsetDays: -120,
      zoneAdjustable: false,
      propertyTied: false,
      dependsOn: [],
      predefinedActions: [],
      sortOrder: 0,
      notes: 'Verify lease is active, access windows are confirmed, and any gate codes or contact info is current before investing time in scouting.',
    },
    // ─── Opener -90: Scouting and cameras ────────────────────────────────────
    {
      id: T.scoutProperty,
      templateId: TEMPLATE_ID,
      title: 'Scout property: identify food sources, travel routes, bedding',
      description: '',
      category: TaskCategory.Scouting,
      timingAnchor: TimingAnchor.OpenerRelative,
      offsetDays: -90,
      zoneAdjustable: false,
      propertyTied: true,
      dependsOn: [T.confirmLease],
      predefinedActions: ['pre_season'],
      sortOrder: 1,
      notes: 'Walk the property to locate food sources, travel corridors, and bedding areas. Mark stand candidate locations.',
    },
    {
      id: T.deployCameras,
      templateId: TEMPLATE_ID,
      title: 'Deploy trail cameras on primary travel routes and scrapes',
      description: '',
      category: TaskCategory.TrailCameras,
      timingAnchor: TimingAnchor.OpenerRelative,
      offsetDays: -90,
      zoneAdjustable: false,
      propertyTied: true,
      dependsOn: [T.scoutProperty],
      predefinedActions: ['deploy'],
      sortOrder: 2,
      notes: 'Fresh batteries and formatted cards. Target travel corridors, pinch points, and any natural or mock scrapes.',
    },
    // ─── Opener -60: Camera pull and stands ──────────────────────────────────
    {
      id: T.cameraCardPull,
      templateId: TEMPLATE_ID,
      title: 'Camera card pull and buck inventory',
      description: '',
      category: TaskCategory.TrailCameras,
      timingAnchor: TimingAnchor.OpenerRelative,
      offsetDays: -60,
      zoneAdjustable: false,
      propertyTied: true,
      dependsOn: [T.deployCameras],
      predefinedActions: ['pull_card', 'analyze'],
      sortOrder: 3,
      notes: 'Review images, catalog bucks by antler characteristics, and identify primary movement patterns to inform stand placement.',
    },
    {
      id: T.hangVerifyStands,
      templateId: TEMPLATE_ID,
      title: 'Hang or verify existing stand locations',
      description: '',
      category: TaskCategory.StandPrep,
      timingAnchor: TimingAnchor.OpenerRelative,
      offsetDays: -60,
      zoneAdjustable: false,
      propertyTied: true,
      dependsOn: [T.scoutProperty],
      predefinedActions: ['hang', 'inspect'],
      sortOrder: 4,
      notes: 'Hang new stands or inspect existing stands for safety — straps, bolts, lifelines. Place based on scouting intel.',
    },
    // ─── Opener -45: Lane trim, license, gear ────────────────────────────────
    {
      id: T.trimShootingLanes,
      templateId: TEMPLATE_ID,
      title: 'Trim shooting lanes on existing stands',
      description: '',
      category: TaskCategory.StandPrep,
      timingAnchor: TimingAnchor.OpenerRelative,
      offsetDays: -45,
      zoneAdjustable: false,
      propertyTied: true,
      dependsOn: [T.hangVerifyStands],
      predefinedActions: ['trim_lanes'],
      sortOrder: 5,
      notes: 'Conservative cuts — maintain cover. Trim only what is necessary after stands are hung so you know exact shooting angles.',
    },
    {
      id: T.purchaseLicense,
      templateId: TEMPLATE_ID,
      title: 'Purchase license and tags',
      description: '',
      category: TaskCategory.RegulatoryAdmin,
      timingAnchor: TimingAnchor.OpenerRelative,
      offsetDays: -45,
      zoneAdjustable: false,
      propertyTied: false,
      dependsOn: [],
      predefinedActions: ['purchase'],
      sortOrder: 6,
      notes: 'Buy all required licenses, tags, and permits. Check for regulation changes from the prior year.',
    },
    {
      id: T.gearTuneUp,
      templateId: TEMPLATE_ID,
      title: 'Weapon tune-up and accuracy check',
      description: '',
      category: TaskCategory.GearEquipment,
      timingAnchor: TimingAnchor.OpenerRelative,
      offsetDays: -45,
      zoneAdjustable: false,
      propertyTied: false,
      dependsOn: [],
      predefinedActions: ['tune'],
      sortOrder: 7,
      notes: 'Bow: cables, strings, and tune. Rifle/shotgun: bore sight and zero. Muzzleloader: clean and test fire. Allow time before opener to correct any issues.',
    },
    // ─── Opener -14: Scent control and final cam pull ─────────────────────────
    {
      id: T.scentControlPrep,
      templateId: TEMPLATE_ID,
      title: 'Scent control prep: wash clothing, spray equipment',
      description: '',
      category: TaskCategory.GearEquipment,
      timingAnchor: TimingAnchor.OpenerRelative,
      offsetDays: -14,
      zoneAdjustable: false,
      propertyTied: false,
      dependsOn: [],
      predefinedActions: ['clean'],
      sortOrder: 8,
      notes: 'Wash all hunting clothing in scent-free detergent. Store in sealed bags. Treat boots and pack with scent-eliminating spray.',
    },
    {
      id: T.finalCamPull,
      templateId: TEMPLATE_ID,
      title: 'Final camera pull and pattern review',
      description: '',
      category: TaskCategory.TrailCameras,
      timingAnchor: TimingAnchor.OpenerRelative,
      offsetDays: -14,
      zoneAdjustable: false,
      propertyTied: true,
      dependsOn: [T.cameraCardPull],
      predefinedActions: ['pull_card', 'analyze'],
      sortOrder: 9,
      notes: 'Last intel pull before season. Confirm buck patterns and timing. Use images to finalize stand selection for opener.',
    },
    // ─── Opener -7: Access route walk ─────────────────────────────────────────
    {
      id: T.accessRouteWalk,
      templateId: TEMPLATE_ID,
      title: 'Access route walk: identify quiet entry/exit paths',
      description: '',
      category: TaskCategory.StandPrep,
      timingAnchor: TimingAnchor.OpenerRelative,
      offsetDays: -7,
      zoneAdjustable: false,
      propertyTied: true,
      dependsOn: [T.trimShootingLanes],
      predefinedActions: [],
      sortOrder: 10,
      notes: 'Walk entry and exit routes at hunt pace. Clear noisy branches and debris. Avoid bedding areas to minimize disturbance.',
    },
    // ─── Opener -1: Pack gear ──────────────────────────────────────────────────
    {
      id: T.packGear,
      templateId: TEMPLATE_ID,
      title: 'Pack gear, confirm stand locations on map',
      description: '',
      category: TaskCategory.GearEquipment,
      timingAnchor: TimingAnchor.OpenerRelative,
      offsetDays: -1,
      zoneAdjustable: false,
      propertyTied: false,
      dependsOn: [],
      predefinedActions: ['pack'],
      sortOrder: 11,
      notes: 'Lay out all gear. Check batteries — headlamp, rangefinder, phone. Confirm stand locations and have a backup plan. Verify license is in pack.',
    },
  ],
};
